import React, { useState, useEffect, useRef } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  DollarSign, 
  ShoppingCart, 
  Target, 
  Activity, 
  Globe, 
  Download, 
  RefreshCw,
  Calendar,
  Filter,
  Eye,
  Clock,
  Percent,
  CreditCard,
  Building
} from 'lucide-react';
import { 
  getAnalyticsDashboard, 
  formatCurrency, 
  formatPercentage, 
  exportAnalyticsData,
  calculateGrowthRate,
  generateChartData
} from '../lib/analytics';
import { getFeeSettings } from '../lib/admin';
import KPICard from '../components/analytics/KPICard';
import LineChart from '../components/analytics/LineChart';
import BarChart from '../components/analytics/BarChart';
import PieChart from '../components/analytics/PieChart';
import DateRangePicker from '../components/analytics/DateRangePicker';
import FilterPanel from '../components/analytics/FilterPanel';
import type { AnalyticsDashboardData, DashboardFilters, DateRange } from '../types/analytics';

export default function AnalyticsDashboard() {
  const [data, setData] = useState<AnalyticsDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [platformFeeRate, setPlatformFeeRate] = useState(0.0131); // Default 1.31%
  const [gatewayFeeRate, setGatewayFeeRate] = useState(0.0269); // Default 2.69%
  const sparklesRef = useRef<HTMLDivElement>(null);

  const [filters, setFilters] = useState<DashboardFilters>({
    dateRange: {
      start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      end: new Date().toISOString().split('T')[0]
    },
    category: 'all',
    userSegment: 'all',
    region: 'all'
  });

  useEffect(() => {
    loadDashboardData();
    loadFeeRates();
    
    const createSparkle = () => {
      if (!sparklesRef.current) return;
      
      const sparkle = document.createElement('div');
      sparkle.className = 'sparkle';
      
      const startX = Math.random() * window.innerWidth;
      const endX = startX + (Math.random() * 100 - 50);
      
      sparkle.style.setProperty('--start-x', `${startX}px`);
      sparkle.style.setProperty('--end-x', `${endX}px`);
      sparkle.style.left = `${startX}px`;
      
      sparklesRef.current.appendChild(sparkle);
      
      setTimeout(() => sparkle.remove(), 3000);
    };

    const interval = setInterval(createSparkle, 200);

    for (let i = 0; i < 10; i++) {
      setTimeout(createSparkle, i * 100);
    }

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [filters]);

  const loadFeeRates = async () => {
    try {
      const { platformFeeRate: pfr, gatewayFeeRate: gfr } = await getFeeSettings();
      setPlatformFeeRate(pfr);
      setGatewayFeeRate(gfr);
    } catch (error) {
      console.error('Error loading fee rates:', error);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const dashboardData = await getAnalyticsDashboard(filters);
      setData(dashboardData);
    } catch (err) {
      console.error('Error loading analytics dashboard:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    await loadFeeRates();
    setRefreshing(false);
  };

  const handleExport = () => {
    if (data) {
      exportAnalyticsData(data, 'analytics_dashboard');
    }
  };

  const handleDateRangeChange = (dateRange: DateRange) => {
    setFilters({ ...filters, dateRange });
  };

  const handleFiltersChange = (newFilters: DashboardFilters) => {
    setFilters(newFilters);
  };

  const resetFilters = () => {
    setFilters({
      dateRange: {
        start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
      },
      category: 'all',
      userSegment: 'all',
      region: 'all'
    });
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-pastel-gradient flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pastel-purple-400"></div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-pastel-gradient flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 text-lg mb-4">{error}</p>
          <button
            onClick={loadDashboardData}
            className="btn-pastel px-4 py-2"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-pastel-gradient">
      {/* Sparkles Container */}
      <div ref={sparklesRef} className="sparkles-container" />

      <div className="container mx-auto px-4 py-8">
        <div className="space-y-8">
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 flex items-center space-x-2 font-poppins">
                <BarChart3 className="w-8 h-8" />
                <span>Analytics Dashboard</span>
              </h1>
              <p className="text-gray-600 mt-2">
                Comprehensive business metrics and performance indicators
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <DateRangePicker
                value={filters.dateRange}
                onChange={handleDateRangeChange}
              />
              
              <FilterPanel
                filters={filters}
                onChange={handleFiltersChange}
                onReset={resetFilters}
                isOpen={showFilters}
                onToggle={() => setShowFilters(!showFilters)}
              />

              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center space-x-2 px-4 py-2 bg-white/60 text-gray-800 rounded-md hover:bg-white/80 disabled:opacity-50 border border-white/40"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>

              <button
                onClick={handleExport}
                className="btn-pastel flex items-center space-x-2 px-4 py-2"
              >
                <Download className="w-4 h-4" />
                <span>Export</span>
              </button>
            </div>
          </div>

          {data && (
            <>
              {/* Real-time Stats Bar */}
              <div className="card-pastel p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Activity className="w-5 h-5 text-pastel-green-600" />
                    <span className="text-gray-800 font-medium font-poppins">Real-time Stats</span>
                  </div>
                  <div className="flex items-center space-x-6 text-sm">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-pastel-green-500 rounded-full animate-pulse"></div>
                      <span className="text-gray-600">
                        {data.realTimeStats.activeUsers} active users
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <ShoppingCart className="w-4 h-4 text-pastel-blue-600" />
                      <span className="text-gray-600">
                        {data.realTimeStats.recentTransactions} recent transactions
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 rounded-full ${
                        data.realTimeStats.systemHealth === 'good' ? 'bg-pastel-green-500' :
                        data.realTimeStats.systemHealth === 'warning' ? 'bg-yellow-400' :
                        'bg-red-400'
                      }`}></div>
                      <span className="text-gray-600">
                        System {data.realTimeStats.systemHealth}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Fee Structure Summary */}
              <div className="card-pastel p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 font-poppins">Transaction Fee Structure</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-pastel-blue-50 border border-pastel-blue-200 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 mb-3">
                      <Building className="w-5 h-5 text-pastel-blue-600" />
                      <span className="text-pastel-blue-700 font-medium font-poppins">Platform Fee</span>
                    </div>
                    <p className="text-2xl font-bold text-pastel-blue-600 font-poppins">{(platformFeeRate * 100).toFixed(2)}%</p>
                    <p className="text-pastel-blue-600 text-sm mt-1">Our service fee</p>
                  </div>
                  
                  <div className="bg-pastel-purple-50 border border-pastel-purple-200 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 mb-3">
                      <CreditCard className="w-5 h-5 text-pastel-purple-600" />
                      <span className="text-pastel-purple-700 font-medium font-poppins">Gateway Fee</span>
                    </div>
                    <p className="text-2xl font-bold text-pastel-purple-600 font-poppins">{(gatewayFeeRate * 100).toFixed(2)}%</p>
                    <p className="text-pastel-purple-600 text-sm mt-1">Payment processing fee</p>
                  </div>
                  
                  <div className="bg-pastel-green-50 border border-pastel-green-200 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 mb-3">
                      <Percent className="w-5 h-5 text-pastel-green-600" />
                      <span className="text-pastel-green-700 font-medium font-poppins">Total Fee</span>
                    </div>
                    <p className="text-2xl font-bold text-pastel-green-600 font-poppins">{((platformFeeRate + gatewayFeeRate) * 100).toFixed(2)}%</p>
                    <p className="text-pastel-green-600 text-sm mt-1">Combined transaction fee</p>
                  </div>
                </div>
              </div>

              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <KPICard
                  title="Daily Revenue"
                  value={formatCurrency(data.kpis.total_revenue.daily)}
                  change={calculateGrowthRate(data.kpis.total_revenue.daily, data.kpis.total_revenue.daily * 0.9)}
                  changeType="increase"
                  icon={<DollarSign className="w-5 h-5" />}
                  description="Today's total revenue"
                />
                
                <KPICard
                  title="Monthly Revenue"
                  value={formatCurrency(data.kpis.total_revenue.monthly)}
                  change={calculateGrowthRate(data.kpis.total_revenue.monthly, data.kpis.total_revenue.monthly * 0.85)}
                  changeType="increase"
                  icon={<TrendingUp className="w-5 h-5" />}
                  description="This month's revenue"
                />

                <KPICard
                  title="Active Users"
                  value={data.kpis.active_users.toLocaleString()}
                  change={data.kpis.customer_acquisition_rate}
                  changeType="increase"
                  icon={<Users className="w-5 h-5" />}
                  description="Currently active users"
                />

                <KPICard
                  title="Conversion Rate"
                  value={formatPercentage(data.kpis.conversion_rates.payment_success)}
                  change={5.2}
                  changeType="increase"
                  icon={<Target className="w-5 h-5" />}
                  description="Payment success rate"
                />

                <KPICard
                  title="Average Order Value"
                  value={formatCurrency(data.kpis.average_order_value)}
                  change={2.8}
                  changeType="increase"
                  icon={<ShoppingCart className="w-5 h-5" />}
                  description="Average transaction value"
                />

                <KPICard
                  title="Customer LTV"
                  value={formatCurrency(data.kpis.customer_lifetime_value)}
                  change={8.5}
                  changeType="increase"
                  icon={<Users className="w-5 h-5" />}
                  description="Customer lifetime value"
                />

                <KPICard
                  title="Daily Active Users"
                  value={data.engagement.daily_active_users.toLocaleString()}
                  change={3.2}
                  changeType="increase"
                  icon={<Activity className="w-5 h-5" />}
                  description="Users active today"
                />

                <KPICard
                  title="Session Duration"
                  value={`${Math.round(data.engagement.session_duration / 60)}m`}
                  change={-2.1}
                  changeType="decrease"
                  icon={<Clock className="w-5 h-5" />}
                  description="Average session length"
                />
              </div>

              {/* Revenue and User Trends */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <LineChart
                  data={generateChartData(
                    data.trends.revenue,
                    data.trends.revenue.map(t => t.period),
                    'value'
                  )}
                  title="Revenue Trend"
                  height={300}
                />

                <LineChart
                  data={generateChartData(
                    data.trends.users,
                    data.trends.users.map(t => t.period),
                    'value'
                  )}
                  title="User Growth"
                  height={300}
                />
              </div>

              {/* Fee Distribution */}
              <div className="card-pastel p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 font-poppins">Fee Distribution</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-white/60 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 mb-3">
                      <Building className="w-5 h-5 text-pastel-blue-600" />
                      <span className="text-gray-800 font-medium font-poppins">Platform Revenue</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-800 font-poppins">
                      {formatCurrency(data.financial.revenue_breakdown.platform_fees)}
                    </p>
                    <p className="text-gray-500 text-sm mt-1">
                      {(platformFeeRate * 100).toFixed(2)}% of transaction volume
                    </p>
                  </div>
                  
                  <div className="bg-white/60 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 mb-3">
                      <CreditCard className="w-5 h-5 text-pastel-purple-600" />
                      <span className="text-gray-800 font-medium font-poppins">Gateway Costs</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-800 font-poppins">
                      {formatCurrency(data.financial.operating_costs)}
                    </p>
                    <p className="text-gray-500 text-sm mt-1">
                      {(gatewayFeeRate * 100).toFixed(2)}% of transaction volume
                    </p>
                  </div>
                  
                  <div className="bg-white/60 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 mb-3">
                      <DollarSign className="w-5 h-5 text-pastel-green-600" />
                      <span className="text-gray-800 font-medium font-poppins">Net Profit</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-800 font-poppins">
                      {formatCurrency(data.financial.revenue_breakdown.platform_fees - data.financial.operating_costs)}
                    </p>
                    <p className="text-gray-500 text-sm mt-1">
                      {formatPercentage(data.financial.profit_margins)} profit margin
                    </p>
                  </div>
                </div>
              </div>

              {/* Engagement Metrics */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="card-pastel p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 font-poppins">User Engagement</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Daily Active Users</span>
                      <span className="text-gray-800 font-medium font-poppins">
                        {data.engagement.daily_active_users.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Monthly Active Users</span>
                      <span className="text-gray-800 font-medium font-poppins">
                        {data.engagement.monthly_active_users.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Bounce Rate</span>
                      <span className="text-gray-800 font-medium font-poppins">
                        {formatPercentage(data.engagement.bounce_rate)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Avg Session Duration</span>
                      <span className="text-gray-800 font-medium font-poppins">
                        {Math.round(data.engagement.session_duration / 60)}m
                      </span>
                    </div>
                  </div>
                </div>

                <div className="card-pastel p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 font-poppins">Retention Rates</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Day 1</span>
                      <span className="text-gray-800 font-medium font-poppins">
                        {formatPercentage(data.engagement.retention_rates.day_1)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Day 7</span>
                      <span className="text-gray-800 font-medium font-poppins">
                        {formatPercentage(data.engagement.retention_rates.day_7)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Day 30</span>
                      <span className="text-gray-800 font-medium font-poppins">
                        {formatPercentage(data.engagement.retention_rates.day_30)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="card-pastel p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 font-poppins">Feature Usage</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Registry Creation</span>
                      <span className="text-gray-800 font-medium font-poppins">
                        {data.engagement.feature_usage.registry_creation.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Gift Claiming</span>
                      <span className="text-gray-800 font-medium font-poppins">
                        {data.engagement.feature_usage.gift_claiming.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Group Gifting</span>
                      <span className="text-gray-800 font-medium font-poppins">
                        {data.engagement.feature_usage.group_gifting.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">RSVP Responses</span>
                      <span className="text-gray-800 font-medium font-poppins">
                        {data.engagement.feature_usage.rsvp_responses.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Financial Insights */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <PieChart
                  data={{
                    labels: ['Registries', 'Group Gifts', 'Platform Fees'],
                    datasets: [{
                      data: [
                        data.financial.revenue_breakdown.registries,
                        data.financial.revenue_breakdown.group_gifts,
                        data.financial.revenue_breakdown.platform_fees
                      ],
                      backgroundColor: ['#8b5cf6', '#ec4899', '#f59e0b']
                    }]
                  }}
                  title="Revenue Breakdown"
                  size={250}
                />

                <div className="card-pastel p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 font-poppins">Financial Health</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Operating Costs</span>
                      <span className="text-gray-800 font-medium font-poppins">
                        {formatCurrency(data.financial.operating_costs)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Profit Margin</span>
                      <span className="text-pastel-green-600 font-medium font-poppins">
                        {formatPercentage(data.financial.profit_margins)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Payment Success Rate</span>
                      <span className="text-gray-800 font-medium font-poppins">
                        {formatPercentage(data.financial.payment_success_rate)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Refund Rate</span>
                      <span className="text-red-500 font-medium font-poppins">
                        {formatPercentage(data.financial.refund_rate)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Customer Analytics */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <BarChart
                  data={{
                    labels: ['New Users', 'Returning Users', 'Power Users'],
                    datasets: [{
                      data: [
                        data.customer.segmentation.new_users,
                        data.customer.segmentation.returning_users,
                        data.customer.segmentation.power_users
                      ],
                      backgroundColor: 'rgba(139, 92, 246, 0.8)'
                    }]
                  }}
                  title="User Segmentation"
                  height={300}
                />

                <div className="card-pastel p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 font-poppins">Geographic Distribution</h3>
                  <div className="space-y-3">
                    {data.customer.geographic_distribution.slice(0, 5).map((location, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <div className="flex items-center space-x-2">
                          <Globe className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-600">{location.country}</span>
                        </div>
                        <div className="text-right">
                          <div className="text-gray-800 font-medium font-poppins">{location.users} users</div>
                          <div className="text-sm text-gray-500">
                            {formatCurrency(location.revenue)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Conversion Funnel */}
              <div className="card-pastel p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 font-poppins">Conversion Funnel</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-800 mb-2 font-poppins">
                      {formatPercentage(data.kpis.conversion_rates.registry_creation)}
                    </div>
                    <div className="text-gray-600">Registry Creation</div>
                    <div className="text-sm text-gray-500">Visitors → Registry Creators</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-800 mb-2 font-poppins">
                      {formatPercentage(data.kpis.conversion_rates.gift_claiming)}
                    </div>
                    <div className="text-gray-600">Gift Claiming</div>
                    <div className="text-sm text-gray-500">Viewers → Gift Claimers</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-800 mb-2 font-poppins">
                      {formatPercentage(data.kpis.conversion_rates.payment_success)}
                    </div>
                    <div className="text-gray-600">Payment Success</div>
                    <div className="text-sm text-gray-500">Attempts → Completed</div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
